import { type InputHTMLAttributes, type ReactElement, useId } from 'react';
import { cn } from '../lib/utils';

export interface CheckboxFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  readonly label: string;
  /** 검증 실패 메시지. 값이 있으면 오류 상태로 렌더링한다. */
  readonly error?: string | undefined;
  readonly containerClassName?: string;
}

/**
 * 라벨 · 체크박스 · 오류 메시지를 하나로 묶은 필드. (약관 동의 등)
 * FormField 와 같은 접근성 원칙 — 오류는 aria-describedby 로 연결한다.
 */
export const CheckboxField = ({
  label,
  error,
  containerClassName,
  className,
  ...inputProps
}: CheckboxFieldProps): ReactElement => {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <div className={cn('flex flex-col gap-1', containerClassName)}>
      <label htmlFor={id} className="flex items-center gap-2 text-sm">
        <input
          id={id}
          type="checkbox"
          className={cn(
            'h-4 w-4 rounded border-input accent-primary',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          {...inputProps}
        />
        <span>{label}</span>
      </label>

      {error ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
};
